/**
 * Place card placeholder resolution, shared by the designer preview and PDF generation so both
 * always produce the same text for a guest.
 */

import type { Event, RSVP } from '@/types';
import { resolveGuestMealChoice, resolveGuestTicketSummary } from '@/lib/place-card-helpers';
import { isMultiTenantEvent } from '@/lib/ticket-mode';

export type PlaceCardSeatingInfo = {
  tableName: string;
  seatNumber: number;
};

export type PlaceCardUnitMeta = { name?: string; number?: string };

export type PlaceCardRank = { name: string; shortName: string };

type SeatingTableLike = {
  id: string;
  name?: string;
  seating?: Array<{ seatNumber: number; rsvpId?: string | null }> | null;
};

export const PLACE_CARD_PLACEHOLDER_REGEX = /\{[a-z_]+\}/gi;

/** rsvpId → table/seat. Later assignments win if a guest appears twice. */
export function buildPlaceCardSeatingMap(tables: SeatingTableLike[]): Map<string, PlaceCardSeatingInfo> {
  const map = new Map<string, PlaceCardSeatingInfo>();
  tables.forEach((table) => {
    const tableName = table.name || `Table ${table.id}`;
    (table.seating ?? []).forEach((seat) => {
      if (seat.rsvpId) {
        map.set(seat.rsvpId, { tableName, seatNumber: seat.seatNumber });
      }
    });
  });
  return map;
}

export function formatPlaceCardEventDate(event: Pick<Event, 'date'> | null | undefined): string {
  if (!event?.date) {
    return '';
  }

  try {
    return event.date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    console.warn('Failed to format event date', error);
    return '';
  }
}

/**
 * Builds the token → value map for one guest. With no guest (designer preview before any RSVPs),
 * name tokens show bracketed hints; a real guest without a recorded name prints as "Guest".
 */
export function buildPlaceholderMap(
  guest: RSVP | null,
  seating: PlaceCardSeatingInfo | undefined,
  event: Event | null,
  unitMeta: PlaceCardUnitMeta,
  ranks: PlaceCardRank[]
): Record<string, string> {
  const composedName = [guest?.firstName, guest?.lastName].filter(Boolean).join(' ');
  const name = (guest?.name ?? composedName).trim();
  const firstName = guest?.firstName ?? name.split(' ')[0] ?? '';
  const lastName = guest?.lastName ?? (name.includes(' ') ? name.slice(name.lastIndexOf(' ') + 1) : '');
  // Prefix (e.g. W.Bro., Bro.) is stored in rsvp.rank; rank/honours in additionalRank
  const rankName = guest?.rank ?? '';
  const rankPrefixShort =
    guest?.rankShortName ?? ranks.find((rank) => rank.name === rankName)?.shortName ?? rankName;
  const multiTenant = isMultiTenantEvent(event);
  const meal = multiTenant ? resolveGuestTicketSummary(guest, event) : resolveGuestMealChoice(guest, event);
  const mealSplit = multiTenant
    ? resolveGuestTicketSummary(guest, event, '\n')
    : resolveGuestMealChoice(guest, event, '\n');

  return {
    '{guest_full_name}': name || (guest ? 'Guest' : '[Guest]'),
    '{guest_first_name}': firstName || (guest ? '' : '[First]'),
    '{guest_last_name}': lastName || (guest ? '' : '[Last]'),
    '{guest_prefix}': rankPrefixShort,
    '{guest_prefix_full}': rankName,
    '{guest_rank}': guest?.additionalRank ?? '',
    '{guest_unit}': guest?.unit ?? unitMeta?.name ?? '',
    '{guest_meal}': meal || '',
    '{guest_meal_split}': mealSplit || '',
    '{guest_table}': seating?.tableName ?? 'Unassigned',
    '{guest_seat}': seating?.seatNumber ? String(seating.seatNumber) : '',
    '{event_name}': event?.name ?? '',
    '{event_date}': formatPlaceCardEventDate(event),
    '{event_location}': event?.location ?? '',
    '{unit_name}': unitMeta?.name ?? '',
    '{unit_number}': unitMeta?.number ?? '',
  };
}

/**
 * Replaces known tokens. Empty values become a single space (keeps line structure); unknown tokens
 * are left as typed so mistakes are visible in the preview and on the printed card alike.
 */
export function applyPlaceholders(content: string | undefined, map: Record<string, string>): string {
  if (!content) {
    return '';
  }

  return content.replace(PLACE_CARD_PLACEHOLDER_REGEX, (token) => {
    const value = map[token];
    if (value === undefined) {
      return token;
    }
    return value === '' ? ' ' : value;
  });
}
